import { useState, useEffect } from 'react';
import { Building2, Users, Newspaper, TrendingUp, ArrowUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState({
    projects: 0,
    leads: 0,
    news: 0
  });
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const [projectsRes, leadsRes, newsRes] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/admin/leads`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/news`)
      ]);

      setStats({
        projects: projectsRes.data.length,
        leads: leadsRes.data.length,
        news: newsRes.data.length
      });

      setRecentLeads(leadsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Total Projects', value: stats.projects, icon: Building2, color: 'from-blue-500 to-blue-600', change: '+12%' },
    { label: 'Total Leads', value: stats.leads, icon: Users, color: 'from-[#20B2AA] to-[#189a93]', change: '+8%' },
    { label: 'News Articles', value: stats.news, icon: Newspaper, color: 'from-purple-500 to-purple-600', change: '+5%' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin h-12 w-12 border-4 border-[#20B2AA] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-black mb-2">Dashboard</h1>
        <p className="text-gray-600">Welcome back! Here's an overview of your website.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 bg-gradient-to-br ${stat.color} rounded-xl`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div className="flex items-center gap-1 text-green-600 text-sm font-medium">
                  <ArrowUp className="h-4 w-4" />
                  {stat.change}
                </div>
              </div>
              <h3 className="text-3xl font-bold text-black mb-1">{stat.value}</h3>
              <p className="text-gray-600 text-sm">{stat.label}</p>
            </div>
          );
        })}
      </div>

      {/* Recent Leads */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-black mb-4 flex items-center gap-2">
          <Users className="h-5 w-5 text-[#20B2AA]" />
          Recent Leads
        </h2>

        {recentLeads.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No leads yet</p>
        ) : (
          <div className="space-y-4">
            {recentLeads.map((lead) => (
              <div
                key={lead.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#20B2AA] rounded-full flex items-center justify-center text-white font-bold">
                    {lead.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-medium text-black">{lead.name}</h3>
                    <p className="text-sm text-gray-600">{lead.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="inline-block px-3 py-1 bg-[#20B2AA]/10 text-[#20B2AA] rounded-full text-xs font-medium">
                    {lead.interest}
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(lead.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
